import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
  alpha,
  CircularProgress,
  Stack,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Refresh,
  NetworkCheck,
  Sync,
  RouterOutlined,
  FilterList as FilterListIcon,
  Lan as LanIcon,
  PeopleAlt as PeopleAltIcon,
  Terminal as TerminalIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Swal from 'sweetalert2';
import { Nas, NasFilters } from '../types';
import { nasService } from '../services/nasService';
import NasForm from '../components/forms/NasForm';
import NasActiveClientsModal from '../components/modals/NasActiveClientsModal';
import RadiusStatusModal from '../components/modals/RadiusStatusModal';
import { MonitorStatusChip, formatMonitorDateTime } from '../utils/monitorDisplayUtils';
import SysKpiCard from '../components/common/SysKpiCard';
import {
  pageShellSx,
  rankingHeaderPaperSx,
  filtersPaperSx,
  listSectionPaperSx,
  listSectionHeaderBarSx,
  rankingTableHeadRowSx,
} from '../styles/theme';

export const NasPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [nasList, setNasList] = useState<Nas[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedNas, setSelectedNas] = useState<Nas | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [nasToDelete, setNasToDelete] = useState<Nas | null>(null);
  const [activeClientsModalOpen, setActiveClientsModalOpen] = useState(false);
  const [selectedNasActiveClients, setSelectedNasActiveClients] = useState<Nas | null>(null);
  const [radiusStatusModalOpen, setRadiusStatusModalOpen] = useState(false);
  // Filtros
  const [filters, setFilters] = useState<NasFilters>({
    search: '',
    type: ''
  });

  const handleViewActiveClients = (nas: Nas) => {
    setSelectedNasActiveClients(nas);
    setActiveClientsModalOpen(true);
  };

  // Carregamento dos dados
  const loadNasList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await nasService.getAll(filters);
      setNasList(data || []);
    } catch (error) {
      enqueueSnackbar('Erro ao carregar lista de NAS', { variant: 'error' });
      setNasList([]);
    } finally {
      setLoading(false);
    }
  }, [filters, enqueueSnackbar]);

  useEffect(() => {
    loadNasList();
  }, [loadNasList]);

  // Handlers
  const handleCreateNew = () => {
    setSelectedNas(null);
    setFormOpen(true);
  };

  const handleEdit = (nas: Nas) => {
    setSelectedNas(nas);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    const isEdit = !!selectedNas;
    const message = isEdit
      ? 'NAS atualizado com sucesso'
      : 'NAS cadastrado com sucesso';

    enqueueSnackbar(message, { variant: 'success' });
    loadNasList();
  };

  const handleDeleteClick = (nas: Nas) => {
    setNasToDelete(nas);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!nasToDelete) return;

    try {
      await nasService.delete(nasToDelete.id);
      enqueueSnackbar('NAS excluído com sucesso', { variant: 'success' });
      setDeleteDialogOpen(false);
      setNasToDelete(null);
      loadNasList();
    } catch (error) {
      enqueueSnackbar('Erro ao excluir NAS', { variant: 'error' });
    }
  };

  const handleTestConnection = async (nas: Nas) => {
    // Mostrar loading
    Swal.fire({
      title: 'Testando Conectividade',
      html: `
        <div style="text-align: center;">
          <p>Testando conectividade com o NAS <strong>${nas.shortname || nas.nasname}</strong></p>
          <p style="color: #666; font-size: 14px;">Por favor, aguarde...</p>
        </div>
      `,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const result = await nasService.testConnection(nas.id);

      if (result.status === 'success') {
        // Sucesso - mostrar detalhes dos testes
        const pingDetails = result.connectivity?.ping;
        const apiDetails = result.connectivity?.api;

        let html = `
          <div style="text-align: left;">
            <div style="margin-bottom: 20px;">
              <h4 style="margin: 0 0 10px 0; color: #2e7d32;">✅ Conectividade Estabelecida</h4>
              <p style="margin: 5px 0; color: #666;">NAS: <strong>${nas.shortname || nas.nasname}</strong></p>
            </div>
            
            <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 15px;">
              <h5 style="margin: 0 0 10px 0; color: #333;">📊 Resultados dos Testes:</h5>
        `;

        // Teste de Ping
        if (pingDetails?.success) {
          html += `
            <div style="margin: 8px 0; display: flex; align-items: center;">
              <span style="color: #2e7d32; margin-right: 8px;">🏓</span>
              <strong>Ping ICMP:</strong> 
              <span style="color: #2e7d32; margin-left: 8px;">OK (${pingDetails.latency}ms)</span>
            </div>
          `;
        } else {
          html += `
            <div style="margin: 8px 0; display: flex; align-items: center;">
              <span style="color: #d32f2f; margin-right: 8px;">❌</span>
              <strong>Ping ICMP:</strong> 
              <span style="color: #d32f2f; margin-left: 8px;">FALHOU (${pingDetails?.error || 'Timeout'})</span>
            </div>
          `;
        }

        // Teste de API (MikroTik)
        if (apiDetails?.skipped) {
          html += `
            <div style="margin: 8px 0; display: flex; align-items: center;">
              <span style="color: #757575; margin-right: 8px;">⚙️</span>
              <strong>API:</strong> 
              <span style="color: #757575; margin-left: 8px;">N/A (${apiDetails?.message || 'API não habilitada'})</span>
            </div>
          `;
        } else if (apiDetails?.success) {
          html += `
            <div style="margin: 8px 0; display: flex; align-items: center;">
              <span style="color: #2e7d32; margin-right: 8px;">🔌</span>
              <strong>API MikroTik:</strong> 
              <span style="color: #2e7d32; margin-left: 8px;">OK (${apiDetails?.message || 'Conexão estabelecida'})</span>
            </div>
          `;
        } else {
          html += `
            <div style="margin: 8px 0; display: flex; align-items: center;">
              <span style="color: #d32f2f; margin-right: 8px;">❌</span>
              <strong>API MikroTik:</strong> 
              <span style="color: #d32f2f; margin-left: 8px;">FALHOU (${apiDetails?.error || apiDetails?.message || 'Erro de conexão'})</span>
            </div>
          `;
        }

        html += `
            </div>
            <div style="background: #e8f5e8; padding: 10px; border-radius: 6px; border-left: 4px solid #2e7d32;">
              <small style="color: #2e7d32;">
                <strong>✓ Status:</strong> Pelo menos um teste passou - NAS está acessível
              </small>
            </div>
          </div>
        `;

        await Swal.fire({
          title: 'Conectividade OK',
          html: html,
          icon: 'success',
          confirmButtonText: 'Fechar',
          width: '500px'
        });

      } else {
        // Erro - mostrar detalhes do que falhou
        const pingDetails = result.connectivity?.ping;
        const apiDetails = result.connectivity?.api;

        let html = `
          <div style="text-align: left;">
            <div style="margin-bottom: 20px;">
              <h4 style="margin: 0 0 10px 0; color: #d32f2f;">❌ Falha na Conectividade</h4>
              <p style="margin: 5px 0; color: #666;">NAS: <strong>${nas.shortname || nas.nasname}</strong></p>
            </div>
            
            <div style="background: #fff3f3; padding: 15px; border-radius: 8px; margin-bottom: 15px;">
              <h5 style="margin: 0 0 10px 0; color: #333;">📊 Resultados dos Testes:</h5>
        `;

        // Teste de Ping
        if (pingDetails?.success) {
          html += `
            <div style="margin: 8px 0; display: flex; align-items: center;">
              <span style="color: #2e7d32; margin-right: 8px;">✅</span>
              <strong>Ping ICMP:</strong> 
              <span style="color: #2e7d32; margin-left: 8px;">OK (${pingDetails.latency}ms)</span>
            </div>
          `;
        } else {
          html += `
            <div style="margin: 8px 0; display: flex; align-items: center;">
              <span style="color: #d32f2f; margin-right: 8px;">❌</span>
              <strong>Ping ICMP:</strong> 
              <span style="color: #d32f2f; margin-left: 8px;">FALHOU (${pingDetails?.error || 'Timeout'})</span>
            </div>
          `;
        }

        // Teste de API (MikroTik)
        if (apiDetails?.skipped) {
          html += `
            <div style="margin: 8px 0; display: flex; align-items: center;">
              <span style="color: #757575; margin-right: 8px;">⚙️</span>
              <strong>API:</strong> 
              <span style="color: #757575; margin-left: 8px;">N/A (${apiDetails?.message || 'API não habilitada'})</span>
            </div>
          `;
        } else if (apiDetails?.success) {
          html += `
            <div style="margin: 8px 0; display: flex; align-items: center;">
              <span style="color: #2e7d32; margin-right: 8px;">✅</span>
              <strong>API MikroTik:</strong> 
              <span style="color: #2e7d32; margin-left: 8px;">OK (${apiDetails?.message || 'Conexão estabelecida'})</span>
            </div>
          `;
        } else {
          html += `
            <div style="margin: 8px 0; display: flex; align-items: center;">
              <span style="color: #d32f2f; margin-right: 8px;">❌</span>
              <strong>API MikroTik:</strong> 
              <span style="color: #d32f2f; margin-left: 8px;">FALHOU (${apiDetails?.error || apiDetails?.message || 'Erro de conexão'})</span>
            </div>
          `;
        }

        html += `
            </div>
            <div style="background: #ffebee; padding: 10px; border-radius: 6px; border-left: 4px solid #d32f2f;">
              <small style="color: #d32f2f;">
                <strong>⚠ Status:</strong> Verifique a configuração do NAS e as credenciais da API
              </small>
            </div>
          </div>
        `;

        await Swal.fire({
          title: 'Falha na Conectividade',
          html: html,
          icon: 'error',
          confirmButtonText: 'Fechar',
          width: '500px'
        });
      }
    } catch (error) {
      await Swal.fire({
        title: 'Erro Interno',
        text: 'Ocorreu um erro inesperado ao testar a conectividade. Tente novamente.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleSyncNas = async (nas: Nas) => {
    const { isConfirmed } = await Swal.fire({
      title: 'Sincronizar MikroTik',
      text: `Deseja sincronizar planos e serviços com o equipamento ${nas.shortname || nas.nasname}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, sincronizar',
      cancelButtonText: 'Cancelar'
    });

    if (!isConfirmed) return;

    Swal.fire({
      title: 'Sincronizando',
      html: 'Aguarde enquanto os dados são enviados para o MikroTik...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const result = await nasService.sync(nas.id);
      if (result.success) {
        Swal.fire('Sucesso', result.message, 'success');
      } else {
        Swal.fire('Erro', result.message, 'error');
      }
    } catch (error: any) {
      Swal.fire('Erro', error.response?.data?.message || 'Erro ao sincronizar equipamento', 'error');
    }
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'error'> = {
      mikrotik: 'primary',
      accel_ppp: 'success',
      other: 'secondary'
    };
    return colors[type] || 'secondary';
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      type: ''
    });
  };

  const totalMikrotik = nasList.filter(n => n.type === 'mikrotik').length;
  const totalApiEnabled = nasList.filter(n => n.enable_api).length;
  const totalOnline = nasList.filter(n => n.monitor_status === 'online').length;

  return (
    <Box sx={pageShellSx}>
      <Paper elevation={0} sx={rankingHeaderPaperSx(theme, 'info')}>
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <RouterOutlined sx={{ fontSize: 120, color: theme.palette.info.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`, boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.35)}` }}>
              <RouterOutlined sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Equipamentos NAS</Typography>
              <Typography variant="caption" color="text.secondary">Gerencie os equipamentos de acesso de rede</Typography>
            </Box>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={loadNasList}
                sx={{ flex: { xs: 1, sm: 'none' }, whiteSpace: 'nowrap' }}
              >
                Atualizar
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<TerminalIcon />}
                onClick={() => setRadiusStatusModalOpen(true)}
                sx={{ flex: { xs: 1, sm: 'none' }, whiteSpace: 'nowrap' }}
              >
                Status Radius
              </Button>
            </Stack>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={handleCreateNew}
              sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap' }}
            >
              Novo NAS
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {!loading && nasList.length > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Total NAS" valor={nasList.length} icone={<RouterOutlined sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="MikroTik" valor={totalMikrotik} icone={<LanIcon sx={{ color: theme.palette.secondary.main }} />} cor={theme.palette.secondary.main} bgCor={alpha(theme.palette.secondary.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="API Habilitada" valor={totalApiEnabled} icone={<NetworkCheck sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Online" valor={totalOnline} icone={<NetworkCheck sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} /></Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={filtersPaperSx(theme)}>
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1.25}>
          <FilterListIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
        </Stack>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Buscar"
              placeholder="IP, hostname ou nome..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} /> }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={filters.type} label="Tipo" onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="accel_ppp">Accel-PPP</MenuItem>
                <MenuItem value="mikrotik">MikroTik</MenuItem>
                <MenuItem value="other">Outro</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>API</InputLabel>
              <Select
                value={filters.enable_api === undefined ? '' : filters.enable_api.toString()}
                label="API"
                onChange={(e) => setFilters(prev => ({ ...prev, enable_api: e.target.value === '' ? undefined : e.target.value === 'true' }))}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Habilitada</MenuItem>
                <MenuItem value="false">Desabilitada</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="outlined" size="small" onClick={clearFilters} fullWidth>Limpar</Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={listSectionPaperSx(theme)}>
        <Box sx={listSectionHeaderBarSx(theme)}>
          <Typography variant="body2" fontWeight={700}>Listagem de NAS</Typography>
          <Chip size="small" label={`${nasList.length} equipamento${nasList.length !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        </Box>

        {loading ? (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">Carregando equipamentos...</Typography>
          </Box>
        ) : !nasList || nasList.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <RouterOutlined sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600}>Nenhum equipamento NAS encontrado</Typography>
            <Typography variant="caption" color="text.secondary">Clique em "Novo NAS" para criar o primeiro</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 850 }}>
              <TableHead>
                <TableRow sx={rankingTableHeadRowSx(theme)}>
                  <TableCell>IP/Hostname</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="center">Monitor</TableCell>
                  <TableCell align="center">Latência</TableCell>
                  <TableCell>Últ. online</TableCell>
                  <TableCell>Últ. offline</TableCell>
                  <TableCell align="center">API</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nasList.map((nas) => (
                  <TableRow key={nas.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {nas.nasname}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {nas.shortname || '-'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={(nas.type || 'other').toUpperCase()}
                          color={getTypeColor(nas.type || 'other')}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <MonitorStatusChip status={nas.monitor_status ?? 'unknown'} />
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">
                          {nas.monitor_latencia_ms != null ? `${nas.monitor_latencia_ms} ms` : '—'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="caption" color="success.main">
                          {formatMonitorDateTime(nas.monitor_ultimo_online_em)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="caption" color="error.main">
                          {formatMonitorDateTime(nas.monitor_ultimo_offline_em)}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        {nas.enable_api ? (
                          <Chip
                            label={`Porta: ${nas.api_port || 'Default'}`}
                            variant="outlined"
                            size="small"
                            color="primary"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        ) : (
                          <Chip
                            label="OFF"
                            variant="outlined"
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem', color: 'text.disabled', borderColor: 'divider' }}
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {nas.description || '-'}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title={!nas.enable_api ? "API MikroTik não habilitada" : "Ver Clientes Online"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleViewActiveClients(nas)}
                                color="success"
                                disabled={!nas.enable_api}
                                sx={{ p: 0.5 }}
                              >
                                <PeopleAltIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Testar Conectividade">
                            <IconButton
                              size="small"
                              onClick={() => handleTestConnection(nas)}
                              color="info"
                              sx={{ p: 0.5 }}
                            >
                              <NetworkCheck sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>

                          {nas.enable_api && (
                            <Tooltip title="Sincronizar MikroTik">
                              <IconButton
                                size="small"
                                onClick={() => handleSyncNas(nas)}
                                color="warning"
                                sx={{ p: 0.5 }}
                              >
                                <Sync sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(nas)}
                              color="primary"
                              sx={{ p: 0.5 }}
                            >
                              <Edit sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(nas)}
                              color="error"
                              sx={{ p: 0.5 }}
                            >
                              <Delete sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Formulário */}
      <NasForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={handleFormSuccess}
        nas={selectedNas}
      />

      {/* Modal de Clientes Online */}
      <NasActiveClientsModal
        open={activeClientsModalOpen}
        nas={selectedNasActiveClients}
        onClose={() => {
          setActiveClientsModalOpen(false);
          setSelectedNasActiveClients(null);
        }}
      />

      {/* Modal de Status do Radius */}
      <RadiusStatusModal
        open={radiusStatusModalOpen}
        onClose={() => setRadiusStatusModalOpen(false)}
      />

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ p: 1.5 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Confirmar Exclusão
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5 }}>
          <DialogContentText sx={{ fontSize: '0.9rem' }}>
            Tem certeza que deseja excluir o NAS "{nasToDelete?.shortname || nasToDelete?.nasname}"?
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" size="small" sx={{ fontSize: '0.8rem' }}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
};

export default NasPage; 